import _ from "lodash";
import {
    GetRowIdParams,
    ICellRendererParams,
    IDetailCellRendererParams,
    ProcessCellForExportParams,
} from "ag-grid-community";
import { SchemaField, SchemaFieldSubtype } from "types/schema";
import DetailsMenu from "containers/legacy-cart/grid-components/DetailsMenu";
import { IGridItem } from "helpers/legacy-cart/line-items/items-conversion-helper";
import { Editors } from "constants/product-collection";
import {
    formatDateRange,
    getSizes,
    isAvailableProductItem,
    getUniqSizes,
    getDetailRowTotals,
    calculateTotalsBySizes,
} from "components/shipment-review/helpers";
import {
    ColumnTypes as ProductItemModalColumnTypes,
    IGridContext,
    IRowData,
    SizeData,
} from "components/shipment-review/types";
import { PinnedDetailGridTotalsRowRenderer } from "components/shipment-review/grid-components/renderers/pinned-detail-grid-totals-row-renderer";
import { SizeCellEditor } from "components/shipment-review/grid-components/editors/size-cell-editor";
import { ShipmentSizeRenderer } from "../grid-components/ShipmentSizeRenderer";
import SelectEditor from "containers/legacy-cart/grid-components/SelectEditor";
import SelectRenderer from "containers/legacy-cart/grid-components/SelectRenderer";
import MultiSelectEditor from "containers/legacy-cart/grid-components/MultiSelectEditor";
import { getSizingGridColDefs, mapLineItemFDMField, setDetailGridRowTotals } from "containers/legacy-cart/helpers";
import { getUniqDateRanges, getDateKey } from "components/shipment-review/shipment-review-grid";
import { IGridEntry, IDeliveryRange } from "types/cart/shipment-review";
import { OtherViewComponents, SizingViewComponents } from "constants/sizingView";
import { InputRenderer } from "containers/legacy-cart/grid-components/InputRenderer";
import { Warehouse } from "@nuorder/client-cart-api/dist/types/modules/brand/models";
import { isDeliveryWindowSuitable } from "helpers/delivery-windows/compare-dates";
import { toDeliveryWindow } from "helpers/legacy-cart/delivery-windows";
import { getBulkUnits } from "helpers/legacy-cart/bulk";
import { PriceWithStrikethroughCell } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/price-with-strikethrough-cell";
import { getDefaultAttributeValue } from "./gridEntry";
import CustomizationRenderer from "components/customization-modal/customization-renderer";
import { getRowClasses } from "helpers/tests";
import { getDbpPrices } from "helpers/legacy-cart/line-items/getPricingMap";
import { DbpDeliveryCellRenderer } from "components/shipment-review/grid-components/renderers/dbp-delivery-cell-renderer";

const DETAILS_PARAMS = {
    ROW_HEIGHT: 50,
    PINNED_ROW_HEIGHT: 32,
};
const SIZE_ROW_CLASS = "detail-row";

export const getVisibilityOptions = (
    item: IGridItem,
): {
    showWarehouse: boolean;
    showPrebook: boolean;
    showBulkUnits: boolean;
} => {
    const { warehouses } = item;

    const showBulkUnits = _.head(item.entries)?.allocated && !item?.deliveryConfig?.hideBulkUnits;
    let showWarehouse = false;
    let showPrebook = false;

    if (!canViewWarehouses(item)) {
        return {
            showWarehouse,
            showPrebook,
            showBulkUnits,
        };
    }

    for (const range of item.shipments || getUniqDateRanges(item.entries)) {
        if (showWarehouse && showPrebook) {
            break;
        }

        const availableWarehouses = _.filter(warehouses, (x) => _.size(getSuitableDeliveryWindows(item, range, x?.id)));

        showWarehouse ||= _.size(availableWarehouses) > 1;
        // TODO: remove `location` within NU-50181. Hide Prebook Column for Bulk Order
        // Bulk Order's line item contains a multiple warehouses, but a single prebook.
        // This leads to indeterminate state. Should be defined by a product
        showPrebook ||= !item.location && _.some(availableWarehouses, canChangePrebook(item, range));
    }

    return {
        showWarehouse,
        showPrebook,
        showBulkUnits,
    };
};

export const getDetailRows = (item: IGridItem, fdm: SchemaField[] = [], isBuildOrderEnabled?: boolean): IRowData[] => {
    const { productId, entries, deliveryConfig, currency, shipments, warehouses, availability, pricesPerSizeMap } =
        item;
    const { enforceAvailableUntil, minimumDeliveryWindow, holdInventory, dbpPriceEnabled } = deliveryConfig;
    const canSelectWarehouses = canViewWarehouses(item);
    const byDates = _.groupBy(entries, (x) => getDateKey(x.shipStart, x.shipEnd));
    const defaultAttributes = _.mapValues(_.head(entries)?.attributes, (attr) => {
        const field = _.find(fdm, { key: `line_items.${attr.field}` });
        return !field || field?.details.subtype === "lookup"
            ? attr // Lookup fields are the same
            : { ...attr, value: getDefaultAttributeValue(field) };
    });
    const detailRows = _.flatMap(shipments || getUniqDateRanges(entries), (range): IRowData[] => {
        const entriesByDate =
            byDates[range.dateKey] ?? getVirtualRowEntries({ item, range }, { attributes: _.clone(defaultAttributes) });
        return _.map(_.groupBy(entriesByDate, getRowId), (entries, rowId, rows): IRowData => {
            const pivot = _.head(entries);
            const row: Omit<IRowData, "totals"> = {
                item: pivot,
                currency,
                id: _.size(rows) > 1 ? rowId : range.dateKey,
                lineItemId: pivot.cartLineItemId,
                productId,
                splitFields: range,
                // for non-buildOrder availability is managed by the appserver
                available:
                    !isBuildOrderEnabled ||
                    isAvailableProductItem(pivot.product, range, enforceAvailableUntil, minimumDeliveryWindow),
                sizes: getSizes(entries),
                enforceAvailableUntil,
                prebook: pivot.prebook,
                warehouse: pivot.warehouse,
                availableWarehouses: canSelectWarehouses
                    ? _.filter(warehouses, (x) => getSuitableDeliveryWindows(item, range, x?.id).length > 0)
                    : [],
                availability,
                lineItemAttributes: _.clone(pivot.attributes),
                shipmentKey: formatDateRange(range.shipStart, range.shipEnd),
                allocated: pivot.allocated,
                isVirtual: !pivot.cartLineItemId,
                canChangePrebook: canChangePrebook(item, range, pivot.warehouse),
                hideInventory: !canSelectWarehouses,
                holdInventory,
                dbpPrices: dbpPriceEnabled && getDbpPrices(entries, currency),
            };
            return { ...row, totals: getDetailRowTotals(row as IRowData, currency) };
        });
    });
    // hide dbp prices
    if (
        dbpPriceEnabled &&
        (_.size(_.uniqBy(detailRows, (x) => _.join(x.dbpPrices?.prices, "-"))) <= 1 || // all deliveries has the same prices
            (_.some(pricesPerSizeMap, (x) => _.size(x.prices) > 1) &&
                _.some(detailRows, (x) => _.size(x.dbpPrices?.prices) > 1))) //  any delivery contains price range and pps range displayed in header
    ) {
        return _.map(detailRows, (x) => _.omit(x, "dbpPrices"));
    }
    return detailRows;
};

export interface DetailRendererParams extends ICellRendererParams {
    data: IGridItem;
    context: IGridContext;
}

const components = {
    [SizingViewComponents.DetailsGridTotalsRowRenderer]: PinnedDetailGridTotalsRowRenderer,
    [SizingViewComponents.PriceWithStrikethrough]: PriceWithStrikethroughCell,
    [Editors.SizeRenderer]: ShipmentSizeRenderer,
    [Editors.SizeCellEditor]: SizeCellEditor,
    [SizingViewComponents.DetailsMenuRenderer]: DetailsMenu,
    [SizingViewComponents.SelectRenderer]: SelectRenderer,
    [SizingViewComponents.MultiSelectEditor]: MultiSelectEditor,
    [SizingViewComponents.SelectEditor]: SelectEditor,
    [SizingViewComponents.InputRenderer]: InputRenderer,
    [SizingViewComponents.DbpDeliveryCellRenderer]: DbpDeliveryCellRenderer,
    [OtherViewComponents.CustomizationsRenderer]: CustomizationRenderer,
};

export const getGridViewDetailRendererParams = (params: DetailRendererParams): Partial<IDetailCellRendererParams> => {
    const uniqueSizes = getUniqSizes(params.data.product);
    const showBottomTotals = params.data.detailsRowsCount > 1;

    return {
        refreshStrategy: "rows",
        detailGridOptions: {
            onFirstDataRendered: (e) => showBottomTotals && setDetailGridRowTotals(e.api, params.data?.currency), // TODO: build "Filtered Totals" depending on data
            columnDefs: getSizingGridColDefs({
                uniqueSizes,
                currency: params.data?.currency,
                fdm: filterEmptyLookupFDM(params.data, params.context.fdm),
                customizations: params.data?.customizations,
                pricesPerSizeMap: params.data?.pricesPerSizeMap,
                ...getVisibilityOptions(params.data),
            }),
            columnTypes: {
                [ProductItemModalColumnTypes.Size]: {},
            },
            rowHeight: DETAILS_PARAMS.ROW_HEIGHT,
            components,
            getRowClass: () => [...getRowClasses(params), SIZE_ROW_CLASS],
            context: { parentApi: params.api, suppressPrice: true, hasSingleDelivery: params.data.hasSingleDelivery },
            pinnedBottomRowData: showBottomTotals
                ? [calculateTotalsBySizes(params.data.entries, params.data?.currency)]
                : [],
            getRowId: ({ data }: CastProp<GetRowIdParams, "data", IRowData>) => data.id,
            getRowHeight: (params) =>
                params.node.rowPinned ? DETAILS_PARAMS.PINNED_ROW_HEIGHT : DETAILS_PARAMS.ROW_HEIGHT,
            popupParent: document.body,
            processCellForClipboard: ({ value }: CastProp<ProcessCellForExportParams, "value", SizeData>): number =>
                value?.quantity || 0,
            suppressCopyRowsToClipboard: true,
            stopEditingWhenCellsLoseFocus: true,
            enableRangeSelection: true,
            enableCellChangeFlash: true,
            valueCache: true,
            suppressContextMenu: true,
        },
        getDetailRowData: ({ successCallback, data }) =>
            successCallback(getDetailRows(data, params.context.fdm, params.context.isBuildOrderEnabled)),
    };
};

const getRowId = (e: IGridEntry) => `${getDateKey(e.shipStart, e.shipEnd)}-${e.cartLineItemId}`;

const getVirtualRowEntries = (
    { item, range }: { item: IGridItem; range: IDeliveryRange },
    defaults: Partial<IGridEntry>,
): IGridEntry[] => {
    const warehouse = item.entries[0]?.defaultWarehouseId;
    const prebooks = _.uniq(_.map(getSuitableDeliveryWindows(item, range, warehouse), "prebook"));
    return _.uniqBy(item.entries, (x) => x.size.size).map((entry) => ({
        ...entry,
        ...defaults,
        shipStart: range.shipStart,
        shipEnd: range.shipEnd,
        quantity: 0,
        cartLineItemId: null,
        // GOA NU-27396: Reset lintItem discount for virtual row. Adding a new line item should not copy line item discount
        // This can happen if a buyer edits an order with a line item promotion, created by an admin.
        discountPercentOverride: null,
        warehouse: _.find(item.warehouses, { id: warehouse }) ?? null, // Default warehouse should always present
        prebook: [false, true].find((value) => prebooks.includes(value)) ?? false, // ATS > Prebook by default
        bulkUnits: entry.bulkUnits ? getBulkUnits() : entry.bulkUnits,
    }));
};

const canViewWarehouses = (item: IGridItem) => {
    const isAllocatedCombined = item.entries?.[0]?.location?.id === null; // undefined for non-bulk, string for by location
    return !isAllocatedCombined || item.deliveryConfig.allocateInventoryAllLocationsCombined;
};

const canChangePrebook = _.curry(
    (item: IGridItem, range: IDeliveryRange, warehouse: Warehouse) =>
        _.uniqBy(getSuitableDeliveryWindows(item, range, warehouse?.id), "prebook").length > 1,
);

const getSuitableDeliveryWindows = (item: IGridItem, range: IDeliveryRange, warehouseId: string) =>
    _.filter(item.availability?.[warehouseId]?.[item.productId]?.deliveryWindows, (dw) =>
        isDeliveryWindowSuitable(toDeliveryWindow(dw), { start: range.shipStart, end: range.shipEnd }),
    );

const filterEmptyLookupFDM = (item: IGridItem, fdm: SchemaField[]): SchemaField[] =>
    _.reject(fdm, (field: SchemaField) => {
        if (field.details.subtype !== SchemaFieldSubtype.Lookup) {
            return false;
        }
        const value = item.attributes?.[mapLineItemFDMField(field)]?.lookupValue;
        return !_.isArray(value) || _.isEmpty(value);
    });

// columns with border + footer + header + bottom padding + scroll
export const getDetailRowHeight = (rowsCount: number) =>
    rowsCount * (DETAILS_PARAMS.ROW_HEIGHT + 1) + (rowsCount > 1 ? DETAILS_PARAMS.PINNED_ROW_HEIGHT : 0) + 34 + 12 + 15;
